/**
 * AccountLoadingFallback - Account-specific Suspense fallback UI
 *
 * Loading state component shown during account management route transitions.
 * Maintains the account layout structure during loading to prevent layout shift.
 *
 * WHY THIS COMPONENT:
 * - Account routes need to maintain sidebar layout during loading
 * - Prevents jarring layout shifts when navigating between account pages
 * - Provides consistent loading experience within account management
 * - Preserves protected route wrapper structure
 *
 * WHY separate from GeneralLoadingFallback:
 * - Maintains account layout structure (sidebar + content area)
 * - Prevents layout shift when account components load
 * - Account-specific spacing and styling
 * - Different responsive behavior (sidebar hidden on mobile)
 *
 * WHY hardcoded colors:
 * - Cannot rely on theme context during Suspense boundary
 * - Theme provider may not be initialized yet
 * - Matches account management UI (#111 background, #1a1c21 sidebar)
 * - Prevents white flash which is harsh on eyes
 *
 * USAGE:
 * Used as fallback prop in React Suspense wrapper around account routes in App.jsx:
 * <Suspense fallback={<AccountLoadingFallback />}>
 *   <AccountManagementLayout>...</AccountManagementLayout>
 * </Suspense>
 *
 * Shows during:
 * - Lazy loading of account components
 * - Navigation between account management pages
 * - Profile, billing, subscription page transitions
 *
 * @see App.jsx - Suspense boundary implementation
 * @see src/features/account/AccountManagementLayout.jsx - Layout structure
 * @see src/ui/GeneralLoadingFallback.tsx - General route variant
 * @see src/ui/Spinner.tsx - Base spinner component
 */

import styled from 'styled-components';
import Spinner from './Spinner';

/**
 * Main container with account layout structure
 *
 * WHY calc(100vh - 80px):
 * - Accounts for 80px header height on desktop
 * - Prevents vertical scroll during loading
 * - Matches actual account layout dimensions
 *
 * WHY fadeIn animation:
 * - Smooth transition when fallback appears
 * - 0.15s is fast enough to feel instant
 * - Reduces perceived loading time
 */
const StyledAccountLoadingFallback = styled.div`
  display: flex;
  min-height: calc(100vh - 80px); // Account for header height
  background-color: #111; // Hardcoded to prevent white flash
  animation: fadeIn 0.15s ease-in;

  @media (max-width: 767px) {
    min-height: calc(100vh - 60px); // Mobile header height
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

/**
 * Sidebar placeholder matching AccountManagementLayout
 *
 * WHY 280px width:
 * - Matches exact sidebar width in AccountManagementLayout
 * - Prevents horizontal layout shift when content loads
 * - Maintains visual consistency
 *
 * WHY border-right:
 * - Matches actual sidebar border styling
 * - Provides visual separation during loading
 * - #2a2c31 matches AccountManagementLayout border color
 */
const SidebarPlaceholder = styled.div`
  width: 280px;
  background-color: #1a1c21; // Hardcoded to prevent white flash
  border-right: 1px solid #2a2c31; // Hardcoded to prevent white flash

  @media (max-width: 1023px) {
    display: none; // Hide sidebar on mobile/tablet
  }
`;

/**
 * Content area with centered spinner
 *
 * WHY flex: 1:
 * - Takes remaining horizontal space after sidebar
 * - Matches AccountManagementLayout content area
 * - Responsive to viewport changes
 *
 * WHY padding 2rem:
 * - Prevents spinner from touching edges
 * - Mobile-friendly spacing
 * - Breathing room around loading indicator
 */
const ContentArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

/**
 * AccountLoadingFallback component
 *
 * WHY function component:
 * - No props needed (Suspense fallback is stateless)
 * - Matches pattern of other loading fallback components
 * - Simplicity is key for fallback UI
 */
function AccountLoadingFallback(): JSX.Element {
  return (
    <StyledAccountLoadingFallback>
      <SidebarPlaceholder />
      <ContentArea>
        <Spinner />
      </ContentArea>
    </StyledAccountLoadingFallback>
  );
}

export default AccountLoadingFallback;
